if (!customElements.get('blankabric-home-hero')) {
  customElements.define('blankabric-home-hero', class extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      const abort = new AbortController();
      const options = { signal: abort.signal };
      const track = this.querySelector('[data-home-hero-track]');
      const slides = [...this.querySelectorAll('[data-home-hero-slide]')];
      const controls = this.querySelector('[data-home-hero-controls]');
      const arrows = this.querySelector('[data-home-hero-arrows]');
      const choices = [...this.querySelectorAll('[data-home-hero-go]')];
      const position = this.querySelector('[data-home-hero-position]');
      const play = this.querySelector('[data-home-hero-play]');
      if (!track || !slides.length || !controls) return;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const photoHoldMs = 2000, photoFadeMs = 1200, photosPerVisit = 3;
      const photoCycleMs = photoHoldMs + photoFadeMs;
      this.style.setProperty('--bb-home-hero-photo-fade', `${photoFadeMs}ms`);
      const photos = slides.map(slide => [...slide.querySelectorAll('[data-home-hero-photo]')]);
      const photoIndexes = photos.map(items => Math.max(0, items.findIndex(item => item.classList.contains('is-active'))));
      let active = 0, target = 0, frame = 0, settle = 0, timer = 0, photoTimer = 0;
      let photosShown = 1;
      let paused = motion.matches, hovered = false, focused = false, dragging = false, visible = true, editing = false;
      const loadPhoto = (photo) => {
        const img = photo?.querySelector('img');
        if (!img) return null;
        if (img.dataset.src) {
          img.loading = 'eager';
          img.srcset = img.dataset.srcset || '';
          img.src = img.dataset.src;
          delete img.dataset.src;
          delete img.dataset.srcset;
        }
        return img;
      };
      const preloadNextPhoto = () => {
        const items = photos[target];
        if (items.length > 1) loadPhoto(items[(photoIndexes[target] + 1) % items.length]);
      };
      const syncPhotoOffer = (slideIndex) => {
        const photo = photos[slideIndex][photoIndexes[slideIndex]];
        if (!photo?.dataset.productTitle) return;
        const title = slides[slideIndex].querySelector('[data-home-hero-product-title]');
        const price = slides[slideIndex].querySelector('[data-home-hero-product-price]');
        const template = photo.querySelector('[data-home-hero-photo-price]');
        if (title) title.textContent = photo.dataset.productTitle;
        if (price && template) price.replaceChildren(template.content.cloneNode(true));
      };
      const advancePhoto = () => {
        const items = photos[target];
        if (items.length < 2) return 'unavailable';
        const current = photoIndexes[target];
        for (let step = 1; step < items.length; step++) {
          const next = (current + step) % items.length;
          const img = loadPhoto(items[next]);
          if (img && !img.complete) return 'pending';
          if (!img?.naturalWidth) continue;
          items[current].classList.remove('is-active');
          items[current].setAttribute('aria-hidden', 'true');
          items[next].classList.add('is-active');
          items[next].setAttribute('aria-hidden', 'false');
          photoIndexes[target] = next;
          syncPhotoOffer(target);
          preloadNextPhoto();
          return 'shown';
        }
        return 'unavailable';
      };
      const stop = () => { clearInterval(timer); clearInterval(photoTimer); timer = 0; photoTimer = 0; };
      const start = () => {
        stop();
        const running = !paused && !hovered && !focused && !dragging && visible && !document.hidden && !editing && !slides[target].querySelector('details[open]');
        if (play) {
          play.dataset.playing = String(!paused);
          play.setAttribute('aria-label', paused ? play.dataset.playLabel : play.dataset.pauseLabel);
        }
        if (position) position.setAttribute('aria-live', running ? 'off' : 'polite');
        if (running) {
          if (photos[target].length > 1) {
            preloadNextPhoto();
            photoTimer = setInterval(() => {
              if (slides.length > 1 && photosShown >= photosPerVisit) {
                go(target + 1);
                return;
              }
              const result = advancePhoto();
              if (result === 'shown') photosShown += 1;
              else if (result === 'unavailable' && slides.length > 1) go(target + 1);
            }, photoCycleMs);
          } else if (slides.length > 1) {
            timer = setInterval(() => go(target + 1), 4000);
          }
        }
      };
      const update = (index) => {
        const changed = active !== index;
        active = index;
        slides.forEach((slide, i) => {
          slide.inert = i !== active;
          slide.setAttribute('aria-hidden', String(i !== active));
        });
        choices.forEach((choice, i) => choice.setAttribute('aria-pressed', String(i === active)));
        if (position) position.textContent = slides[active].getAttribute('aria-label');
        syncPhotoOffer(active);
        if (changed) { target = active; photosShown = 1; start(); }
      };
      const go = (index, instant = false) => {
        target = (index + slides.length) % slides.length;
        update(target);
        track.scrollTo({ left: slides[target].offsetLeft - slides[0].offsetLeft, behavior: instant || motion.matches ? 'instant' : 'smooth' });
      };
      const sync = () => {
        let nearest = 0, distance = Infinity;
        slides.forEach((slide, index) => {
          const delta = Math.abs(slide.getBoundingClientRect().left - track.getBoundingClientRect().left);
          if (delta < distance) { nearest = index; distance = delta; }
        });
        update(nearest);
        target = nearest;
      };
      choices.forEach((choice, index) => choice.addEventListener('click', () => { go(index); start(); }, options));
      this.querySelector('[data-home-hero-prev]')?.addEventListener('click', () => { go(target - 1); start(); }, options);
      this.querySelector('[data-home-hero-next]')?.addEventListener('click', () => { go(target + 1); start(); }, options);
      play?.addEventListener('click', () => { paused = !paused; start(); }, options);
      track.addEventListener('keydown', (event) => {
        if (event.target !== track) return;
        const index = { ArrowRight: target + 1, ArrowLeft: target - 1, Home: 0, End: slides.length - 1 }[event.key];
        if (index === undefined) return;
        event.preventDefault(); go(index); start();
      }, options);
      track.addEventListener('scroll', () => { clearTimeout(settle); settle = setTimeout(sync, 140); }, { ...options, passive: true });
      track.addEventListener('scrollend', sync, options);
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; start(); } }, options);
      this.addEventListener('pointerleave', () => { hovered = false; start(); }, options);
      track.addEventListener('pointerdown', () => { dragging = true; start(); }, options);
      const release = () => { if (dragging) { dragging = false; start(); } };
      window.addEventListener('pointerup', release, options);
      window.addEventListener('pointercancel', release, options);
      this.querySelectorAll('details').forEach(details => details.addEventListener('toggle', start, options));
      this.addEventListener('focusin', () => { focused = true; start(); }, options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) { focused = false; start(); } }, options);
      document.addEventListener('visibilitychange', start, options);
      motion.addEventListener('change', () => { paused = motion.matches; start(); }, options);
      window.addEventListener('resize', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => go(active, true)); }, options);
      this.addEventListener('shopify:block:select', event => {
        const index = slides.findIndex(slide => slide.dataset.homeHeroBlock === event.detail.blockId);
        if (index >= 0) { editing = true; start(); go(index, true); }
      }, options);
      this.addEventListener('shopify:block:deselect', () => { editing = false; start(); }, options);
      const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting; start();
      }, { threshold: 0.15 }) : null;
      observer?.observe(this);
      controls.hidden = false;
      if (arrows) arrows.hidden = false;
      update(0);
      start();
      this.cleanup = () => { abort.abort(); observer?.disconnect(); stop(); cancelAnimationFrame(frame); clearTimeout(settle); };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
}
